<script setup>

import {nextTick, onMounted, reactive} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import { PhoneNumber } from "@/Utils/PhoneNumber.js";
import {Permissions} from "@/Utils/Permissions";
import {FormValidation} from "@/Utils/FormValidation";
import CreateUserFromPersonComponent from "@/Components/UserManagement/CreateUserFromPersonComponent.vue";
import UpdateUserRolesComponent from "@/Components/UserManagement/UpdateUserRolesComponent.vue";
import {ToastUtil} from "@/Utils/ToastUtil";
import {userStore} from "@/Stores/UserStore";
import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
  UpdateUserAttributesCommand,
  DeleteUserAttributesCommand,
  GetUserAttributeVerificationCodeCommand,
  VerifyUserAttributeCommand
} from "@aws-sdk/client-cognito-identity-provider";
import QRCode from 'qrcode';

//Texting can only be turned on by the person themselves (their consent); anyone who can edit a profile can turn it off.
//The API enforces this too; this keeps the switch from offering what will be refused.
const isOwnProfile = () => props.contextIdA === 'myprofile';

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const adUser = userStore.userInfo.adUser;

const GetClientTextReminderCookieVal = () => {
  const key = "clientReminders_textingEnabled";     //Value of 0 means not handled, 1 == enabled, 2 == reminder
  const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'));
  return match ? parseInt(decodeURIComponent(match[1]), 10) : 0;
};
let displayTextingReminderPopup =
    GetClientTextReminderCookieVal() === 0 &&
    userStore.textMessagingEnableReminder;


const data = reactive({
  date: new Date(),
  createUserDialogStatus: 'closed',
  updateRolesDialogStatus: 'closed',
  updateUserRolesDialog: 0,

  editAddress: false,
  editCellPhone: false,
  editTextingEnabled: false,
  textingNumber: null,     //our texting number, for the "text START" note
  editCompany: false,
  editDOB: false,
  editDeployedAddress: false,
  editNpn: false,
  editNpnDate: false,
  editDriverLicenseExpiration: false,
  editDriverLicenseNumber: false,
  editDriverLicenseState: false,
  editEmail: false,
  editEmergencyContact: false,
  editHomePhone: false,
  editLegalName: false,
  editName: false,
  editXactnetAddress: false,
  editSymbilityEmail: false,
  editSymbilityVendorID: false,
  editedItem: {},
  // editedItem: new Person(),
  editProfileForm: false,
  loading: false,
  loadingAddress: false,
  loadingCellPhone: false,
  loadingCompany: false,
  loadingDOB: false,
  loadingDeployedAddress: false,
  loadingDeployedAddressRemove: false,
  loadingNpn: false,
  loadingNpnDate: false,
  loadingDriverLicenseExpiration: false,
  loadingDriverLicenseNumber: false,
  loadingDriverLicenseState: false,
  loadingEmail: false,
  loadingEmergencyContact: false,
  loadingFullLegalName: false,
  loadingHomePhone: false,
  loadingName: false,
  loadingXactnetAddress: false,
  loadingSymbilityEmail: false,
  loadingSymbilityVendorID: false,
  menu: false,
  person: {},
  removeDeployed: false,
  states: [
    'AL',
    'AK',
    'AS',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FM',
    'FL',
    'GA',
    'GU',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MH',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'MP',
    'OH',
    'OK',
    'OR',
    'PW',
    'PA',
    'PR',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VI',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ],
  valid: true,
  mfaAppEnabled: false,
  mfaSmsEnabled: false,
  mfaPreferredApp: false,
  mfaPreferredSms: false,
  mfaPreferredCurrent: '',
  mfaPreferredEditing: false,
  mfaPreferenceChangeApp: false,
  mfaPreferenceChangeSms: false,
  mfaCurrentPhone: '',
  mfaPhoneNumber: '',
  mfaCognitoPhone: '',
  mfaCognitoPhoneValid: false,
  mfaCognitoPhoneUpdated: false,
  mfaSmsCode: '',
  mfaSmsCodeIsValidating: false,
  mfaSmsCodeSent: false,
  mfaSmsCodeVerified: false,
  mfaLoading: false,
  mfaEditing: false,
  mfaShowQr: false,
  mfaShowSms: false,
  mfaQrCodeUrl: '',
  mfaSecretCode: '',
  mfaAppCodeValid: false,
  mfaAppCode: '',
  mfaAppCodeIsValidating: false,
  mfaAppVerified: false,
  mfaTCPAChecked: false,
  textingEnabled: false,

  //controls the Dialog pop-up as soon as this page is rendered
  showTextingEnableDialog: displayTextingReminderPopup
});

const client = new CognitoIdentityProviderClient({ region: "us-east-1" });

const getCurrentUserMFA = async() => {
  data.mfaLoading = true;
  data.mfaEditing = false;
  data.mfaPreferredApp = false;
  data.mfaPreferredSms = false;
  const command = new GetUserCommand(
      {
        AccessToken: userStore.accessToken.toString()
      }
  );
  try{
    const response = await client.send(command);
    let hasMFAOptions = true;
    let hasUserMFASettingList = true;
    if(Object.hasOwn(response,'MFAOptions')){
      if(response.MFAOptions[0].DeliveryMedium==='SMS'){
        data.mfaSmsEnabled = true
        data.mfaTCPAChecked = true
      }
    }
    else{
      hasMFAOptions = false;
    }
    if(Object.hasOwn(response,'UserMFASettingList')){
      if(response.UserMFASettingList.includes('SMS_MFA')){
        data.mfaSmsEnabled = true
        data.mfaTCPAChecked = true
      }
      if(response.UserMFASettingList.includes('SOFTWARE_TOKEN_MFA')){
        data.mfaAppEnabled = true
      }
    }
    else{
      hasUserMFASettingList = false;
    }
    if(response.PreferredMfaSetting){
      if(response.PreferredMfaSetting === 'SMS_MFA'){
        data.mfaPreferredSms = true
        data.mfaPreferredCurrent = 'sms'
      }
      if(response.PreferredMfaSetting === 'SOFTWARE_TOKEN_MFA'){
        data.mfaPreferredApp = true
        data.mfaPreferredCurrent = 'app'
      }
    }
    else{
      if(data.mfaSmsEnabled){
        data.mfaPreferredSms = true
        data.mfaPreferredCurrent = 'sms'
      }
    }
    if(Object.hasOwn(response,'UserAttributes')){
      response.UserAttributes.map((obj)=>{
          if(obj.Name){
            if(obj.Name === 'phone_number' && obj.Value.length===12){
              displayCurrentCognitoPhone(obj.Value)
            }
          }
      })
    }
    //If neither MFA sotrage comes back on the user, assume that App is enabled and preferred
    if(!hasMFAOptions && !hasUserMFASettingList){
      data.mfaAppEnabled = true
      data.mfaPreferredApp = true
      data.mfaPreferredCurrent = 'app'
      data.mfaSmsEnabled = false
    }
  }
  catch(e){
    await ToastUtil.error('Could not retrieve MFA Settings',5000);
  }
  data.mfaLoading = false;
}

const getQrCode = async() => {
  const command = new AssociateSoftwareTokenCommand({
    AccessToken: userStore.accessToken.toString()
  });

  try{
    const response = await client.send(command);
    data.mfaSecretCode = response.SecretCode
    data.mfaQrCodeUrl = await QRCode.toDataURL(`otpauth://totp/${userStore.userInfo.username}:${userStore.userInfo.name}?secret=${response.SecretCode}&issuer=${userStore.userInfo.username}`);

  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to generate the QR code',5000)
  }
}

const setUserMfaPref = async (type) => {
  try{
    let input = {}
    if(type === 'SOFTWARE_TOKEN_MFA' ){
      input = {
        AccessToken: userStore.accessToken.toString(),
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true
        },
        SMSMfaSettings: {
          Enabled: data.mfaSmsEnabled,
          PreferredMfa: false
        }
      }
    }

    if(type === 'SMS_MFA' ){
      input = {
        AccessToken: userStore.accessToken.toString(),
        SoftwareTokenMfaSettings: {
          Enabled: data.mfaAppEnabled,
          PreferredMfa: false
        },
        SMSMfaSettings: {
          Enabled: true,
          PreferredMfa: true
        }
      }
    }

    const command = new SetUserMFAPreferenceCommand(input);
    const response = await client.send(command);
    if(response.$metadata.httpStatusCode === 200){
      if(type === 'SOFTWARE_TOKEN_MFA'){
        data.mfaPreferredApp = true;
        data.mfaPreferredSms = false;
      }
      if(type === 'SMS_MFA' ){
        data.mfaPreferredApp = false;
        data.mfaPreferredSms = true;
      }
      await ToastUtil.successMsg('Successfully changed your preferred MFA method!',10000)
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to set your MFA preference',10000)
  }
}

const setUserMfaPrefUI = async (type) => {
  data.mfaLoading = true;
  await setUserMfaPref(type);
  await getCurrentUserMFA();
}

const verifySoftwareToken = async () => {
  data.mfaAppCodeIsValidating = true;
  try{
    const command = new VerifySoftwareTokenCommand({
      AccessToken: userStore.accessToken.toString(),
      UserCode: data.mfaAppCode,
    });

    const response = await client.send(command);
    if (response.Status === "SUCCESS") {
      data.mfaAppVerified = true;
      data.mfaAppEnabled = true;
      data.mfaShowQr = false
      if(data.mfaPreferredApp || !data.mfaSmsEnabled){
        await setUserMfaPref('SOFTWARE_TOKEN_MFA')
      }
      if(!data.mfaPreferredApp && data.mfaSmsEnabled){
        await setUserMfaPref('SMS_MFA')
      }
      await getCurrentUserMFA();
      await ToastUtil.successMsg('App MFA successfully added',10000)
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to verify the app code',10000)
  }
  data.mfaAppCodeIsValidating = false;
}

const updateSmsPhoneNumber = async () => {
  data.mfaAppCodeIsValidating = true;
  try{
    const command = new UpdateUserAttributesCommand({
      AccessToken: userStore.accessToken.toString(),
      UserAttributes: [
        {
          Name: 'phone_number',
          Value: data.mfaCognitoPhone
        }
      ]
    });

    const response = await client.send(command);
    if (response.$metadata.httpStatusCode === 200) {
      data.mfaCognitoPhoneUpdated = true;
      await ToastUtil.successMsg('MFA Phone number successfully updated',10000)
      await sendSmsCode();
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to verify the app code',10000)
  }
  data.mfaAppCodeIsValidating = false;
}

const sendSmsCode = async () => {
  data.mfaAppCodeIsValidating = true;
  try{
    const command = new GetUserAttributeVerificationCodeCommand({
      AccessToken: userStore.accessToken.toString(),
      AttributeName: 'phone_number',
    });

    const response = await client.send(command);
    if (response.$metadata.httpStatusCode === 200) {
      data.mfaSmsCodeSent = true
      await ToastUtil.successMsg('SMS MFA Code was sent to '+data.mfaCognitoPhone,10000)
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to verify the app code',10000)
  }
  data.mfaAppCodeIsValidating = false;
}

const verifySmsCode = async () => {
  data.mfaAppCodeIsValidating = true;
  try{
    const command = new VerifyUserAttributeCommand({
      AccessToken: userStore.accessToken.toString(),
      AttributeName: 'phone_number',
      Code: data.mfaSmsCode
    });

    const response = await client.send(command);
    if (response.$metadata.httpStatusCode === 200) {
      data.mfaSmsCodeSent = true
      await ToastUtil.successMsg('SMS MFA Code is verified!',10000)
      if(data.mfaPreferredSms || !data.mfaAppEnabled){
        await setUserMfaPref('SMS_MFA')
      }
      if(!data.mfaPreferredSms && data.mfaAppEnabled){
        await setUserMfaPref('SOFTWARE_TOKEN_MFA')
      }
      data.mfaCognitoPhoneUpdated = false;
      data.mfaSmsCodeSent = false;
      data.mfaCognitoPhoneValid = false;
      data.mfaSmsCode = '';
      await ToastUtil.successMsg('SMS phone number is verified!');
      await getCurrentUserMFA()
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to verify the app code',10000)
  }
  data.mfaAppCodeIsValidating = false;
}

const disableSms = async () => {
  //User must have MFA app set to disable SMS
  try{
    if(data.mfaAppEnabled && data.mfaSmsEnabled && !data.mfaTCPAChecked){
      let input = {
        AccessToken: userStore.accessToken.toString(),
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true
        },
        SMSMfaSettings: {
          Enabled: false,
          PreferredMfa: false
        }
      }
      const command = new SetUserMFAPreferenceCommand(input);
      const response = await client.send(command);
      if(response.$metadata.httpStatusCode === 200){
        await ToastUtil.successMsg('You have successfully un-enrolled from SMS MFA!',10000);
        await removeSmsPhoneNumber();
        await getCurrentUserMFA();
      }
    }
    if(!data.mfaAppEnabled && data.mfaSmsEnabled && !data.mfaTCPAChecked){
      data.mfaEditing = false;
      await ToastUtil.warningMsg('You must set up an Authenticator App MFA before un-enrolling from SMS MFA.',20000);
      data.mfaTCPAChecked = true;
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to un-enroll from SMS MFA',10000);
    await getCurrentUserMFA();
  }

}

const removeSmsPhoneNumber = async () => {
  try{
    const command = new DeleteUserAttributesCommand({
      AccessToken: userStore.accessToken.toString(),
      UserAttributeNames: [
        "phone_number"
      ]
    });

    const response = await client.send(command);
    if (response.$metadata.httpStatusCode === 200) {
      data.mfaPhoneNumber = '';
      data.mfaCurrentPhone = '';
      data.mfaCognitoPhone = '';
      data.mfaShowSms = false;
      data.mfaSmsEnabled = false;
      await ToastUtil.successMsg('Phone number removed from SMS MFA!',10000);
      await getCurrentUserMFA();
    }
  }
  catch(e){
    await ToastUtil.error('An error occurred while attempting to remove the SMS Phone Number',10000)
    await getCurrentUserMFA();
  }
}

const acceptEmergencyContactNumber = () => {
  let x = data.editedItem.emergencycontactnumber.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  data.editedItem.emergencycontactnumber = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
}

const acceptHomeNumber = () => {
  let x = data.editedItem.homephone.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  data.editedItem.homephone = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
}

//Mobile number only (texting needs a valid US number); the profile's other phone fields keep the shared rule
const mobilePhoneRule = (value) => {
  const problem = PhoneNumber.problem(value);
  return problem ? `Enter a valid US mobile number: ${problem}` : true;
};

const acceptMobileNumber = (event) => {
  //Don't reformat while deleting, or it puts back the ")" they just removed
  if(event?.inputType?.startsWith('delete')) return;

  //Format as they type; the API stores it as +1XXXXXXXXXX
  data.editedItem.mobilephone = PhoneNumber.formatAsYouType(data.editedItem.mobilephone);
}

const acceptSmsNumber = () => {
  let x = data.mfaPhoneNumber.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  data.mfaPhoneNumber = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
  formatCognitoPhoneNumber();
}

const acceptAppCode = () => {
  let regex = /^\d{6}$/
  data.mfaAppCodeValid = regex.test(data.mfaAppCode);
  if(data.mfaAppCodeValid){
    verifySoftwareToken()
  }
}

const acceptSmsCode = () => {
  let regex = /^\d{6}$/
  if(regex.test(data.mfaSmsCode)){
    verifySmsCode()
  }
}

const formatDate = (date) => {
  if (!date) return null
  return new Date(date).toLocaleDateString();
}

const parseDateWithoutTZ = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const displayDate = (dateStr) => {
  const date = parseDateWithoutTZ(dateStr);
  return date ? date.toLocaleDateString() : null;
}

const parseDateWithTZ = (dateStr) => {
  if (!dateStr) return null;
  dateStr = dateStr.split('T')[0];
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const displayTzDate = (dateStr) => {
  const date = parseDateWithTZ(dateStr);
  return date ? date.toLocaleDateString() : null;
}


//The texting switch: start an edit if there isn't one, but never restart one, or it would throw away
//anything typed but not saved yet (like a new mobile number)
const onTextingToggle = () => {
  data.editTextingEnabled = true;

  if(!data.isEditing) {
    editItem();
  }

  data.editedItem.texting_enabled = data.person.texting_enabled;
};

const editItem = () => {
  data.isEditing = true;
  data.editedItem = Object.assign({}, data.person)

  //Stored as +18655551234; edit it as (865) 555-1234. The API stores it back as +1... when saved.
  //Numbers that aren't valid stay exactly as stored so they can be corrected.
  data.editedItem.mobilephone = PhoneNumber.toDisplay(data.editedItem.mobilephone);
  /*
  data.editedItem.formatdob = formatDate(data.editedItem.formatdob) === null ? '' : formatDate(data.editedItem.formatdob)
  data.editedItem.formatexpiration = formatDate(data.editedItem.formatexpiration) === null ? '' : formatDate(data.editedItem.formatexpiration)
   */
  if(data.removeDeployed) {
    data.editedItem.deployedstreetaddress = null;
    data.editedItem.deployedcity = null;
    data.editedItem.deployedstate = null;
    data.editedItem.deployedzip = null;
    data.editedItem.deployedlat = '';
    data.editedItem.deployedlong = '';
  }
}

// removeDeployedAddress() will set deployedaddress, deployedcity, deployedstate, deployedzip, deployedlat, and deployedlong to "null"
const removeDeployedAddress = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to remove deployed address?'
  )
  if (deleteMessage === true) {
    data.loadingDeployedAddressRemove = true
    ApiWrapper.post('user-card','edit-profile', {
      editedItem: item,
    })
    .then((response) => {
      listAll()
      data.removeDeployed = false;
      data.loadingDeployedAddressRemove = false;
      ToastUtil.successMsg('Removed deployed address');
    })
    .catch((err) => {
      ToastUtil.error(err);
      data.loadingDeployedAddressRemove = false;
    })
  }
}

const save = () => {
  //Nothing being edited (e.g. another field's save or cancel already ended the edit): nothing to save, so don't leave a spinner running
  if (!data.isEditing) {
    stopLoading();
    return;
  }

  //The mobile number and the texting switch are saved together (both are in editedItem), so saving either one closes both
  data.editCellPhone = false;
  data.editTextingEnabled = false;

  data.loading = true;
  if (data.isEditing) {
    let encodedSearch = encodeURI(
        (data.editedItem.streetaddress ?? '').replace('#', '') +
        `, ` +
        data.editedItem.city +
        `, ` +
        data.editedItem.state +
        ` ` +
        data.editedItem.zip
    )
    let encodedDeployedSearch = encodeURI(
        (data.editedItem.deployedstreetaddress ?? '').replace('#', '') +
        `, ` +
        data.editedItem.deployedcity +
        `, ` +
        data.editedItem.deployedstate +
        ` ` +
        data.editedItem.deployedzip
    )
    ApiWrapper.getRaw('https://api.mapbox.com/geocoding/v5/mapbox.places/' +
        encodedSearch +
        '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
    )
    .then(function (response) {
      let lat = ''
      let long = ''
      if(response){
        if(response.features.length > 0){
          lat = response.features[0].center[1]
          long = response.features[0].center[0]
        }
      }


      ApiWrapper.getRaw('https://api.mapbox.com/geocoding/v5/mapbox.places/' +
          encodedDeployedSearch +
          '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
      )
      .then(function (response) {
        let deployedlat = ''
        let deployedlong = ''
        if(response){
          if(response.features.length > 0){
            deployedlat = response.features[0].center[1]
            deployedlong = response.features[0].center[0]
          }
        }

        ApiWrapper.post('user-card', 'edit-profile', {
              editedItem: data.editedItem,
              lat: `${lat}`,
              long: `${long}`,
              deployedlat: `${deployedlat}`,
              deployedlong: `${deployedlong}`
            })
            .then((response) => {
              listAll();
              ToastUtil.successMsg('Updated profile');
            })
            .catch((err) => {
              ToastUtil.error(err);
              stopLoading();
              listAll(); //reload, so a refused change (like turning on texting) doesn't stay showing
            });
      })
      .catch((err) => {
        ToastUtil.error(err);
        stopLoading();
      })
    })
    .catch((err) => {
      ToastUtil.error(err);
      stopLoading();
    })
  }
  close();
}

const listAll = () => {
  data.loading = true;
  let contextIdA = props.contextIdA;
  // let contextIdB = new URLSearchParams(window.location.search).get(
  //     'contextIdB'
  // );
  if(contextIdA === null){
    ApiWrapper.post('user-card', 'user-card', { myProfile: true })
        .then((response) => {
            data.person = response.person;
            let tempObj = data.person
            for (let [key, value] of Object.entries(tempObj)) {
              if(value === "null" || value === "undefined" || value === "undefined\n" || value === null){
                tempObj[key] = ''
              } else {
                tempObj[key] = value
              }
            }
            data.person = tempObj;
            data.textingEnabled = data.person.texting_enabled;
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(()=>{
          stopLoading();
        });
  } else {
    let inputData = {};
    if(props.contextIdA === 'myprofile') {
      inputData.myProfile = true;
    }
    else {
      inputData.personId = props.contextIdA;
    }
    ApiWrapper.post('user-card', 'user-card', inputData)
        .then((response) => {
            data.person = response.person;
            let tempObj = data.person
            for (let [key, value] of Object.entries(tempObj)) {
              if(value === "null" || value === "undefined" || value === "undefined\n" || value === null){
                tempObj[key] = ''
              } else {
                tempObj[key] = value
              }
            }
            data.person = tempObj;
            data.textingEnabled = data.person.texting_enabled;
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(()=>{
          stopLoading();
        });
  }
}

const stopLoading = () => {
  data.loading = false;
  data.loadingAddress = false;
  data.loadingCellPhone = false;
  data.loadingCompany = false;
  data.loadingDeployedAddress = false;
  data.loadingDOB = false;
  data.loadingNpn = false;
  data.loadingNpnDate = false;
  data.loadingDriverLicenseExpiration = false;
  data.loadingDriverLicenseNumber = false;
  data.loadingDriverLicenseState = false;
  data.loadingEmail = false;
  data.loadingEmergencyContact = false;
  data.loadingFullLegalName = false;
  data.loadingHomePhone = false;
  data.loadingName = false;
  data.loadingXactnetAddress = false;
  data.loadingSymbilityEmail = false;
  data.loadingSymbilityVendorID = false;
}

const formatPhoneNumber = (phoneNumberString) => {
  let cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return null
}

const close = () => {
  data.editProfileForm = false;
  nextTick(() => {
    // data.editedItem = Object.assign({}, data.defaultItem);
    // data.editedItem = new Person();
    data.isEditing = false;
  });
}

const mfaSetupDisplay = (mfaSelection) => {
  data.mfaEditing = true;

  if(mfaSelection==='app'){
    data.mfaShowQr = true
    getQrCode()
  }
  if(mfaSelection==='sms'){
    data.mfaShowSms = true
  }
}

const formatCognitoPhoneNumber = () => {
  data.mfaCognitoPhone = '+1'+data.mfaPhoneNumber.toString()
      .replaceAll('(','')
      .replaceAll(')','')
      .replaceAll(' ','')
      .replaceAll('-','')
  if(data.mfaCognitoPhone.length===12){
    data.mfaCognitoPhoneValid = true
  }
  else{
    data.mfaCognitoPhoneValid = false
    data.mfaSmsCodeSent = false
  }
}

const displayCurrentCognitoPhone = (cognitoPhone) => {
  data.mfaCurrentPhone = '('+cognitoPhone.substring(2,5)+') '+cognitoPhone.substring(5,8)+'-'+cognitoPhone.substring(8,12)
  if(data.mfaPhoneNumber === ''){
    data.mfaPhoneNumber = data.mfaCurrentPhone
  }
}

//Our texting number, so someone who once texted STOP knows where to text START
const getTextingNumber = () => {
  ApiWrapper.get('conversations', 'my-texting')
      .then((response) => {
        data.textingNumber = PhoneNumber.toDisplay(response.texting_number) || null;
      })
      .catch(() => {}); //only used for the note, which is skipped without it
};

//Value of 0 means not handled, 1 == enabled, 2 == reminder
const writeReminderCookie = (val) => {
  const key = "clientReminders_textingEnabled";
  const days = 0.5;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${key}=${encodeURIComponent(val)}; expires=${expires}; path=/; SameSite=Lax`;
};

const ReminderLaterDialog = () => {
  userStore.setTextMessagingReminder(false);
  data.showTextingEnableDialog = false;
  writeReminderCookie(2);
};

const AcceptTextMessagingDialog = () => {
  data.person.texting_enabled = true;
  onTextingToggle();
  data.editTextingEnabled = false;
  save();
  data.textingEnabled = data.person.texting_enabled;
  userStore.setTextMessagingReminder(false);
  writeReminderCookie(1);
  data.showTextingEnableDialog = false;
};

onMounted(()=>{
  listAll();
  if(isOwnProfile()){
    getTextingNumber();
  }
  if(!adUser){
    getCurrentUserMFA();
  }
})

</script>

<template>
  <v-container id="ApiWrapper-resize-target" fluid>
    <CreateUserFromPersonComponent :personId="data.person.id" :status="data.createUserDialogStatus" @closed="data.createUserDialogStatus='closed'" @newUser="listAll()"/>
    <UpdateUserRolesComponent :personId="data.person.id" :status="data.updateRolesDialogStatus" :updateNum="data.updateUserRolesDialog" @closed="data.updateRolesDialogStatus='closed';listAll();"/>

    <v-card class="elevation-2" :loading="data.loading">
      <v-row>
        <v-col xl="6">
          <v-card-title class="">
            <div v-if="!data.editName" class="display-1">{{data.person.name}}</div>
            <v-text-field v-if="data.editName" v-model="data.editedItem.name" label="Name"></v-text-field>
            <v-btn
                v-if="!data.editName && data.person.name && Permissions.checkPermissions(['users.edit-profile'])"
                variant="elevated"
                icon size="x-small"
                class="ml-2"
                :loading="data.loadingName"
                @click="data.editName = true; editItem()"
            >
                <!-- Note: This should go on v-btn :disabled="data.person.aduser" -->
              <v-icon size="large">mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="data.editName" class="primary" @click="data.editName = false; data.loadingName = true; save()">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-btn v-if="data.editName" class="error" @click="data.editName = false; data.isEditing = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </v-col>
        <v-col xl="6" style="display:flex; justify-content:space-between">
          <div class="ma-4" style="position: absolute; right: 0;">
            <v-btn color="success" dark class="ml-2 mb-2" @click="data.createUserDialogStatus = 'open';" v-if="!data.person.cognitouser && Permissions.checkPermissions(['users.create'])">
              Create Account
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="data.person.cognitouser || data.person.aduser">
        <v-col xl="12" class="pb-0">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text pt-0">
              <span>Roles</span>

              <v-btn
                  v-if="Permissions.checkPermissions(['users.update-roles'])"
                  variant="plain"
                  size="small"
                  class="ml-2"
                  @click="data.updateRolesDialogStatus = 'open';data.updateUserRolesDialog++;"
              >
                <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
              </v-btn>
            </v-card-title>
            <v-row wrap>
              <v-col xl="12" class="">
                <v-chip
                    v-if="data.person.is_admin === true"
                    prepend-icon="mdi-star"
                    class="ma-1"
                    size="small"
                    color="green"
                >
                  AD Admin
                </v-chip>
                <template v-for="role in data.person.roles" :key="role">
                  <v-chip
                      class="ma-1"
                      size="small"
                      color="blue"
                  >
                    {{ role }}
                  </v-chip>
                </template>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col xl="12" class="pb-0">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text pt-0">Personal Info</v-card-title>
            <v-row wrap>
              <v-col md="12" xl="6" class="py-0">
                <v-list two-line>
                  <!-- Full Legal Name -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-account</v-icon>
                    </template>
                    <v-list-item-title v-if="!data.editLegalName">{{data.person.legalname}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editLegalName">Full Legal Name</v-list-item-subtitle>
                    <v-text-field v-if="data.editLegalName" v-model="data.editedItem.legalname" label="Full Legal Name"></v-text-field>
                    <template v-slot:append v-if="data.editLegalName">
                      <v-icon size="x-large" color="primary" @click="data.editLegalName = false; data.loadingFullLegalName = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editLegalName = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-list-item-action v-if="!data.editLegalName">
                        <v-btn
                            v-if="Permissions.checkPermissions(['users.edit-profile'])"
                            variant="plain" size="small"
                            :loading="data.loadingFullLegalName"
                            @click="data.editLegalName = true; editItem()">
                          <!-- Note: This should go on v-btn :disabled="data.person.aduser" -->

                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>

                  <!-- Address -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-map-marker</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editAddress">{{data.person.streetaddress}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editAddress">{{data.person.city}}, {{data.person.state}}
                      {{data.person.zip}}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="!data.editAddress">Address</v-list-item-subtitle>
                    <v-text-field v-if="data.editAddress" v-model="data.editedItem.streetaddress" label="Address"></v-text-field>
                    <v-text-field v-if="data.editAddress" v-model="data.editedItem.city" label="City"></v-text-field>
                    <v-autocomplete
                        v-if="data.editAddress"
                        v-model="data.editedItem.state"
                        label="State"
                        :items="data.states"
                        auto-select-first
                    ></v-autocomplete>
                    <v-text-field v-if="data.editAddress" v-model="data.editedItem.zip" label="Zip"></v-text-field>

                    <template v-slot:append v-if="data.editAddress">
                      <v-icon size="x-large" color="primary" @click="data.editAddress = false; data.loadingAddress = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editAddress = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-list-item-action v-if="!data.editAddress">
                        <v-btn v-if="Permissions.checkPermissions(['users.edit-profile'])" variant="plain" size="small" :loading="data.loadingAddress" @click="data.editAddress = true; editItem()">
                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>

                  <!-- Mobile Phone -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-cellphone</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editCellPhone">{{ PhoneNumber.toDisplay(data.person.mobilephone) }}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editCellPhone">Mobile</v-list-item-subtitle>
                    <!-- Old numbers were saved in all kinds of formats; texting needs a valid US number -->
                    <!-- A plain div, not v-list-item-subtitle: subtitles are cut off at one line -->
                    <div v-if="!data.editCellPhone && PhoneNumber.savedProblem(data.person.mobilephone)" class="text-caption text-error mt-1">
                      Please re-enter this mobile number: {{ PhoneNumber.savedProblem(data.person.mobilephone) }}.
                    </div>
                    <v-form
                        v-if="data.editCellPhone"
                        ref="form"
                        v-model="data.valid"
                        lazy-validation
                    >
                      <v-text-field
                          v-if="data.editCellPhone"
                          v-model="data.editedItem.mobilephone"
                          label="Mobile Phone"
                          :rules="[mobilePhoneRule]"
                          @input="acceptMobileNumber"
                      >
                      </v-text-field>
                    </v-form>
                    <template v-slot:append v-if="data.editCellPhone">
                      <v-icon size="x-large" color="primary" @click="data.editCellPhone = false; data.loadingCellPhone = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editCellPhone = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-icon>mdi-message-text</v-icon>
                      <v-list-item-action>
                        <v-btn v-if="!data.editCellPhone && Permissions.checkPermissions(['users.edit-profile'])" variant="plain" size="small" :loading="data.loadingCellPhone" @click="data.editCellPhone = true; editItem()">
                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                  <!-- Text messages: part of the mobile number above, so it's indented under it and boxed -->
                  <v-list-item class="pr-0 pt-0">
                    <v-sheet rounded class="texting-box ml-14 pa-3">
                      <v-switch
                        label="Receive text messages at this mobile number"
                        color="primary"
                        class="ma-0 pa-0"
                        density="compact"
                        hide-details
                        v-model="data.person.texting_enabled"
                        :disabled="!isOwnProfile() && !data.person.texting_enabled"
                        @change="onTextingToggle"
                      ></v-switch>
                      <!-- Twilio blocks texts to anyone who texted STOP until they text START; the switch can't undo that -->
                      <p v-if="isOwnProfile() && data.editTextingEnabled && data.person.texting_enabled && data.textingNumber" class="text-caption ma-0 mt-1">
                        If you've ever texted <b>STOP</b> to {{ data.textingNumber }}, text <b>START</b> to it to receive messages again.
                      </p>
                      <v-alert v-if="!isOwnProfile()" type="warning" variant="tonal" density="compact" class="text-caption mt-2">
                        Only {{ data.person.name }} can turn on text messages, from their own profile. You can turn them off.
                      </v-alert>
                      <p class="text-caption text-medium-emphasis ma-0 mt-2">
                        By enabling text messages, I agree to receive dispatch communications, notifications, and alerts from Associated Adjusters Network LLC at the phone number provided.
                        Message frequency varies. Message & data rates may apply. Reply <b>STOP</b> to opt out or HELP for help.
                        For more detail, read the <a href="https://associatedadjusting.com/privacy" target="_blank">Privacy Policy</a> and the <a href="https://associatedadjusting.com/terms" target="_blank">Terms of Service</a>.
                      </p>
                    </v-sheet>
                    <template v-slot:append v-if="data.editTextingEnabled">
                      <v-icon size="x-large" color="primary" @click="data.editTextingEnabled = false; save(); data.textingEnabled = data.person.texting_enabled;">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editTextingEnabled = false; data.isEditing = false; data.person.texting_enabled = data.textingEnabled">mdi-close</v-icon>
                    </template>
                  </v-list-item>

                  <v-divider class="my-2"/>

                  <!-- Home Phone -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-phone</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editHomePhone">{{formatPhoneNumber(data.person.homephone)}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editHomePhone">Home</v-list-item-subtitle>
                    <v-form
                        v-if="data.editHomePhone"
                        ref="form"
                        v-model="data.valid"
                        lazy-validation
                    >
                      <v-text-field
                          v-model="data.editedItem.homephone"
                          label="Home Phone"
                          :rules="[FormValidation.phoneRules]"
                          @input="acceptHomeNumber"
                      >
                      </v-text-field>
                    </v-form>

                    <template v-slot:append v-if="data.editHomePhone">
                      <v-icon size="x-large" color="primary" @click="data.editHomePhone = false; data.loadingHomePhone = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editHomePhone = false; data.isEditing = false">mdi-close</v-icon>
                    </template>

                    <template v-slot:append v-else>
                      <v-icon>mdi-message-text</v-icon>
                      <v-list-item-action>
                        <v-btn variant="plain" size="small" v-if="!data.editHomePhone && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingHomePhone" @click="data.editHomePhone = true; editItem()">
                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col md="12" xl="6" class="py-0 pl-0">
                <v-list two-line>
                  <!-- Email -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-email</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editEmail">{{data.person.email}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editEmail">Email</v-list-item-subtitle>
                    <v-text-field v-if="data.editEmail" v-model="data.editedItem.email" label="Email"></v-text-field>

                    <template v-slot:append v-if="data.editEmail">
                      <v-icon size="x-large" color="primary" @click="data.editEmail = false; data.loadingEmail = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editEmail = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-list-item-action>
                        <v-btn
                            variant="plain"
                            size="small"
                            v-if="!data.editEmail && Permissions.checkPermissions(['users.edit-profile'])"
                            :loading="data.loadingEmail"
                            @click="data.editEmail = true; editItem()"
                        >
                          <!-- Note: This should go on v-btn :disabled="data.person.aduser" -->

                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>

                  </v-list-item>

                  <!-- DOB -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-calendar</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editDOB">{{displayDate(data.person.formatdob)}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editDOB">DOB</v-list-item-subtitle>
                    <div v-if="data.editDOB">
                      <v-menu
                          ref="menu"
                          v-model="data.menu"
                          :close-on-content-click="false"
                          :return-value="data.editedItem.formatdob"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                      >
                        <template v-slot:activator="{ on, props }">
                          <v-text-field
                              v-model="data.editedItem.formatdob"
                              label="DOB"
                              persistent-hint
                              v-bind="props"
                              clearable
                              prepend-icon="mdi-calendar"
                              v-on="on"
                          >
                          </v-text-field>
                        </template>
                        <v-date-picker
                            v-model="data.date"
                            scrollable
                            @update:modelValue="data.date=$event;data.editedItem.formatdob = formatDate(data.date)"
                        >
                        </v-date-picker>
                      </v-menu>
                    </div>

                    <template v-slot:append v-if="data.editDOB">
                      <v-icon size="x-large" color="primary" @click="data.editDOB = false; data.loadingDOB = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editDOB = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-list-item-action>
                        <v-btn variant="plain" size="small" v-if="!data.editDOB && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingDOB" @click="data.editDOB = true; editItem(); data.date = parseDateWithoutTZ(data.person.formatdob);">
                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>



                  </v-list-item>

                  <!-- Emergency Contact -->
                  <v-list-item class="pr-0">
                    <template v-slot:prepend>
                      <v-icon size="large" color="indigo">mdi-account</v-icon>
                    </template>

                    <v-list-item-title v-if="!data.editEmergencyContact">{{data.person.emergencycontactname}}</v-list-item-title>
                    <v-list-item-title v-if="!data.editEmergencyContact">{{formatPhoneNumber(data.person.emergencycontactnumber)}}</v-list-item-title>
                    <v-list-item-subtitle v-if="!data.editEmergencyContact">Emergency Contact</v-list-item-subtitle>
                    <v-form
                        v-if="data.editEmergencyContact"
                        ref="form"
                        v-model="data.valid"
                        lazy-validation
                    >
                      <v-text-field v-model="data.editedItem.emergencycontactname" label="Emergency Contact Name"></v-text-field>
                      <v-text-field
                          v-model="data.editedItem.emergencycontactnumber"
                          label="Emergency Contact Number"
                          :rules="[FormValidation.phoneRules]"
                          @input="acceptEmergencyContactNumber"
                      >
                      </v-text-field>
                    </v-form>

                    <template v-slot:append v-if="data.editEmergencyContact">
                      <v-icon size="x-large" color="primary" @click="data.editEmergencyContact = false; data.loadingEmergencyContact = true; save()">mdi-plus</v-icon>
                      <v-spacer class="px-3"/>
                      <v-icon size="x-large" color="error" @click="data.editEmergencyContact = false; data.isEditing = false">mdi-close</v-icon>
                    </template>
                    <template v-slot:append v-else>
                      <v-list-item-action>
                        <v-btn variant="plain" size="small" v-if="!data.editEmergencyContact && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingEmergencyContact" @click="data.editEmergencyContact = true; editItem()">
                          <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </template>



                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12" xl="6" class="py-0 pl-0">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text py-0">Driver License Info</v-card-title>
            <v-list two-line>
              <!-- Driver License Number -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-smart-card</v-icon>
                </template>

                <v-list-item-title v-if="!data.editDriverLicenseNumber">{{data.person.driverlicensenumber}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editDriverLicenseNumber">Driver License Number</v-list-item-subtitle>
                <v-text-field v-if="data.editDriverLicenseNumber" v-model="data.editedItem.driverlicensenumber" label="Driver License Number"></v-text-field>


                <template v-slot:append v-if="data.editDriverLicenseNumber">
                  <v-icon size="x-large" color="primary" @click="data.editDriverLicenseNumber = false; data.loadingDriverLicenseNumber = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editDriverLicenseNumber = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editDriverLicenseNumber && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingDriverLicenseNumber" @click="data.editDriverLicenseNumber = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>

              <!-- Driver License State -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-map-marker</v-icon>
                </template>

                <v-list-item-title v-if="!data.editDriverLicenseState">{{data.person.driverlicensestate}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editDriverLicenseState">Driver License State</v-list-item-subtitle>
                <v-autocomplete
                    v-if="data.editDriverLicenseState"
                    v-model="data.editedItem.driverlicensestate"
                    label="Driver License State"
                    :items="data.states"
                    auto-select-first
                ></v-autocomplete>

                <template v-slot:append v-if="data.editDriverLicenseState">
                  <v-icon size="x-large" color="primary" @click="data.editDriverLicenseState = false; data.loadingDriverLicenseState = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editDriverLicenseState = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editDriverLicenseState && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingDriverLicenseState" @click="data.editDriverLicenseState = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>



              </v-list-item>

              <!-- Driver License Expiration -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-calendar</v-icon>
                </template>

                <v-list-item-title v-if="!data.editDriverLicenseExpiration">{{displayDate(data.person.formatexpiration)}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editDriverLicenseExpiration">Driver License Expiration</v-list-item-subtitle>
                <div v-if="data.editDriverLicenseExpiration">
                  <v-menu
                      ref="menu2"
                      v-model="data.menu2"
                      :close-on-content-click="false"
                      :return-value="data.editedItem.formatexpiration"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                  >
                    <template v-slot:activator="{ on, props }">
                      <v-text-field
                          v-model="data.editedItem.formatexpiration"
                          label="Driver License Exp"
                          persistent-hint
                          v-bind="props"
                          clearable
                          prepend-icon="mdi-calendar"
                          v-on="on"
                      >
                      </v-text-field>
                    </template>
                    <v-date-picker
                        v-model="data.date"
                        scrollable
                        @update:modelValue="data.date=$event;data.editedItem.formatexpiration = formatDate(data.date)"
                    >
                    </v-date-picker>
                  </v-menu>
                </div>

                <template v-slot:append v-if="data.editDriverLicenseExpiration">
                  <v-icon size="x-large" color="primary" @click="data.editDriverLicenseExpiration = false; data.loadingDriverLicenseExpiration = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editDriverLicenseExpiration = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editDriverLicenseExpiration && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingDriverLicenseExpiration" @click="data.editDriverLicenseExpiration = true; editItem(); data.date = parseDateWithoutTZ(data.person.formatexpiration);">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>

              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col md="12" xl="6" class="py-0">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text py-0">Business Info</v-card-title>
            <v-list two-line>
              <!-- Company -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-account-group</v-icon>
                </template>

                <v-list-item-title v-if="!data.editCompany">{{data.person.company}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editCompany">Company</v-list-item-subtitle>
                <v-text-field v-if="data.editCompany" v-model="data.editedItem.company" label="Company"></v-text-field>

                <template v-slot:append v-if="data.editCompany">
                  <v-icon size="x-large" color="primary" @click="data.editCompany = false; data.loadingCompany = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editCompany = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editCompany && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingCompany" @click="data.editCompany = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>

              </v-list-item>

              <!-- Deployed Address -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-map-marker</v-icon>
                </template>

                <v-list-item-title v-if="!data.editDeployedAddress">{{data.person.deployedstreetaddress}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editDeployedAddress">{{data.person.deployedcity}} {{data.person.deployedstate}}
                  {{data.person.deployedzip}}</v-list-item-subtitle>
                <v-list-item-subtitle v-if="!data.editDeployedAddress">Deployed Address</v-list-item-subtitle>
                <v-text-field v-if="data.editDeployedAddress" v-model="data.editedItem.deployedstreetaddress" label="Deployed Address"></v-text-field>
                <v-text-field v-if="data.editDeployedAddress" v-model="data.editedItem.deployedcity" label="Deployed City"></v-text-field>
                <v-autocomplete
                    v-if="data.editDeployedAddress"
                    v-model="data.editedItem.deployedstate"
                    label="Deployed State"
                    :items="data.states"
                    auto-select-first
                ></v-autocomplete>
                <v-text-field v-if="data.editDeployedAddress" v-model="data.editedItem.deployedzip" label="Deployed Zip"></v-text-field>


                <template v-slot:append v-if="data.editDeployedAddress">
                  <v-icon size="x-large" color="primary" @click="data.editDeployedAddress = false; data.loadingDeployedAddress = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editDeployedAddress = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <div v-if="!data.editDeployedAddress">
                    <v-list-item-action class="mr-0">
                      <v-btn v-if="Permissions.checkPermissions(['users.edit-profile'])" variant="plain" size="small" :loading="data.loadingDeployedAddressRemove" @click="data.removeDeployed = true; editItem(); data.item = data.editedItem; removeDeployedAddress(data.item)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <v-btn v-if="Permissions.checkPermissions(['users.edit-profile'])" variant="plain" size="small" :loading="data.loadingDeployedAddress" @click="data.editDeployedAddress = true; editItem()">
                        <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </div>
                </template>
              </v-list-item>

              <!-- Xactnet Address -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-map-marker</v-icon>
                </template>

                <v-list-item-title v-if="!data.editXactnetAddress">{{data.person.xactnetaddress}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editXactnetAddress">Xactnet Address</v-list-item-subtitle>
                <v-textarea
                    v-if="data.editXactnetAddress"
                    prepend-inner-icon="mdi-pencil"
                    filled
                    clearable
                    clear-icon="mdi-close"
                    label="Xactnet Address"
                    hint="No character limit"
                    v-model="data.editedItem.xactnetaddress"
                    auto-grow
                >
                </v-textarea>

                <template v-slot:append v-if="data.editXactnetAddress">
                  <v-icon size="x-large" color="primary" @click="data.editXactnetAddress = false; data.loadingXactnetAddress = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editXactnetAddress = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editXactnetAddress && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingXactnetAddress" @click="data.editXactnetAddress = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>

              <!-- Symbility Email -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-map-marker</v-icon>
                </template>

                <v-list-item-title v-if="!data.editSymbilityEmail">{{data.person.symbility_email}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editSymbilityEmail">Symbility Email</v-list-item-subtitle>
                <v-list-item-subtitle v-if="!data.editSymbilityEmail">For Internal Symbiltiy Users</v-list-item-subtitle>
                <v-text-field v-if="data.editSymbilityEmail" v-model="data.editedItem.symbility_email" label="Symbility Email"></v-text-field>

                <template v-slot:append v-if="data.editSymbilityEmail">
                  <v-icon size="x-large" color="primary" @click="data.editSymbilityEmail = false; data.loadingSymbilityEmail = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editSymbilityEmail = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editSymbilityEmail && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingSymbilityEmail" @click="data.editSymbilityEmail = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>

              <!-- Symbility Vendor ID -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-account-group</v-icon>
                </template>

                <v-list-item-title v-if="!data.editSymbilityVendorID">{{data.person.symbility_vendor_id}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editSymbilityVendorID">Symbility Vendor ID</v-list-item-subtitle>
                <v-list-item-subtitle v-if="!data.editSymbilityVendorID">For External Symbility Users</v-list-item-subtitle>
                <v-list-item-subtitle v-if="!data.editSymbilityVendorID">(Defaults to this if set)</v-list-item-subtitle>
                <v-text-field v-if="data.editSymbilityVendorID" v-model="data.editedItem.symbility_vendor_id" label="Symbility Vendor ID"></v-text-field>

                <template v-slot:append v-if="data.editSymbilityVendorID">
                  <v-icon size="x-large" color="primary" @click="data.editSymbilityVendorID = false; data.loadingSymbilityVendorID = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editSymbilityVendorID = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editSymbilityVendorID && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingSymbilityVendorID" @click="data.editSymbilityVendorID = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>

            </v-list>
          </v-card>
        </v-col>
        <v-col md="12" xl="6" class="py-0">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text py-0">NPN Info</v-card-title>
            <v-list two-line>
              <!-- NPN Number -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-smart-card</v-icon>
                </template>

                <v-list-item-title v-if="!data.editNpn">{{data.person.npn}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editNpn">NPN Number</v-list-item-subtitle>
                <v-text-field v-if="data.editNpn" v-model="data.editedItem.npn" label="NPN Number"></v-text-field>


                <template v-slot:append v-if="data.editNpn">
                  <v-icon size="x-large" color="primary" @click="data.editNpn = false; data.loadingNpn = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editNpn = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editNpn && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingNpn" @click="data.editNpn = true; editItem()">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>

              <!-- NPN Entered Date -->
              <v-list-item class="pr-0">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-calendar</v-icon>
                </template>

                <v-list-item-title v-if="!data.editNpnDate">{{displayTzDate(data.person.npn_updated_on)}}</v-list-item-title>
                <v-list-item-subtitle v-if="!data.editNpnDate">NPN Updated On</v-list-item-subtitle>
                <div v-if="data.editNpnDate">
                  <v-menu
                      ref="menu2"
                      v-model="data.menu2"
                      :close-on-content-click="false"
                      :return-value="data.editedItem.formatnpndate"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                  >
                    <template v-slot:activator="{ on, props }">
                      <v-text-field
                          v-model="data.editedItem.formatnpndate"
                          label="NPN Updated On"
                          persistent-hint
                          v-bind="props"
                          clearable
                          prepend-icon="mdi-calendar"
                          v-on="on"
                      >
                      </v-text-field>
                    </template>
                    <v-date-picker
                        v-model="data.date"
                        scrollable
                        @update:modelValue="data.date=$event;data.editedItem.formatnpndate = formatDate(data.date)"
                    >
                    </v-date-picker>
                  </v-menu>
                </div>

                <template v-slot:append v-if="data.editNpnDate">
                  <v-icon size="x-large" color="primary" @click="data.editNpnDate = false; data.loadingNpnDate = true; save()">mdi-plus</v-icon>
                  <v-spacer class="px-3"/>
                  <v-icon size="x-large" color="error" @click="data.editNpnDate = false; data.isEditing = false">mdi-close</v-icon>
                </template>
                <template v-slot:append v-else>
                  <v-list-item-action>
                    <v-btn variant="plain" size="small" v-if="!data.editNpnDate && Permissions.checkPermissions(['users.edit-profile'])" :loading="data.loadingNpnDate" @click="data.editNpnDate = true; editItem(); data.date = parseDateWithoutTZ(data.person.formatnpndate);">
                      <v-icon size="large" color="grey lighten-1">mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>

              </v-list-item>

            </v-list>
          </v-card>
        </v-col>
        <v-col md="12" xl="6" class="py-0 pl-0" v-if="!adUser">
          <v-card flat class="ma-2">
            <v-card-title class="primary--text py-0">Multi-Factor Authentication Settings</v-card-title>
            <v-list lines="two" density="compact" class="overflow-hidden">
              <!-- MFA Authenticator App -->
              <v-list-item class="pr-0" density="compact">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo" density="compact" class="mt-5">mdi-qrcode</v-icon>
                </template>
                <template v-slot:default>
                  <v-row>
                    <v-col md="3">
                      <v-btn
                          :prepend-icon="data.mfaAppEnabled ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline'"
                          @click="mfaSetupDisplay('app'); data.mfaAppCode=''"
                          :loading="data.mfaLoading"
                      >
                        <v-list-item-subtitle>Authenticator App</v-list-item-subtitle>
                      </v-btn>
                    </v-col>
                    <v-col md="3">
                      <v-btn
                          v-if="data.mfaAppEnabled"
                          type="success"
                          :prepend-icon="data.mfaPreferredCurrent === 'app' ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline'"
                          :loading="data.mfaLoading"
                          :disabled="!data.mfaSmsEnabled || (data.mfaAppEnabled && data.mfaPreferredApp)"
                          @click="setUserMfaPrefUI('SOFTWARE_TOKEN_MFA')"
                      >
                        <v-list-item-subtitle>Preferred</v-list-item-subtitle>
                      </v-btn>
                    </v-col>
                    <v-col md="6"></v-col>
                  </v-row>

                </template>

              </v-list-item>

              <!-- MFA SMS Message -->
              <v-list-item class="pr-0" density="compact">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo" class="mt-5">mdi-message</v-icon>
                </template>
                <template v-slot:default>
                  <v-row>
                    <v-col md="3">
                      <v-btn
                          :prepend-icon="data.mfaSmsEnabled ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline'"
                          @click="mfaSetupDisplay('sms')"
                          :loading="data.mfaLoading"
                      >
                        <v-list-item-subtitle>SMS Message</v-list-item-subtitle>
                      </v-btn>
                    </v-col>
                    <v-col md="3">
                      <v-btn
                          v-if="data.mfaSmsEnabled"
                          type="success"
                          text="Preferred"
                          :prepend-icon="data.mfaPreferredCurrent === 'sms' ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline'"
                          :loading="data.mfaLoading"
                          :disabled="!data.mfaAppEnabled || (data.mfaSmsEnabled && data.mfaPreferredSms)"
                          @click="setUserMfaPrefUI('SMS_MFA')"
                      >
                        <v-list-item-subtitle>Preferred</v-list-item-subtitle>
                      </v-btn>
                    </v-col>
                    <v-col md="6"></v-col>
                  </v-row>


                </template>
              </v-list-item>
              <v-list-item v-if="data.mfaCurrentPhone !== ''" density="compact">
                <template v-slot:prepend>
                  <v-icon size="large" color="indigo">mdi-phone</v-icon>
                </template>
                <template v-slot:default>
                    <v-list-item-subtitle>SMS Phone number: +1 {{data.mfaCurrentPhone}}</v-list-item-subtitle>
                </template>
              </v-list-item>

            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
  <v-dialog
      v-model="data.mfaShowQr"
      max-width="1000"
      @click:outside="data.mfaShowQr = false; data.mfaEditing = false; data.mfaPreferredEditing=false; data.mfaPreferenceChangeApp=false; getCurrentUserMFA()"
  >
    <v-card class="overflow-y-hidden">
      <v-card-title class="d-flex justify-space-between align-center">
        Scan QR Code
        <v-btn
            class="align-right"
            icon="mdi-close"
            variant="text"
            @click="data.mfaShowQr = false; data.mfaEditing = false; data.mfaPreferredEditing=false; data.mfaPreferenceChangeApp=false; getCurrentUserMFA()"
        ></v-btn>
      </v-card-title>

      <v-card-text>
        Scan the QR code below to set up your Claimslink Multi Factor Authentication Code in your authenticator app.
        <br>
        <br>
        Microsoft Authenticator and Google Authenticator apps are supported. Other authenticator apps are not recommended.
      </v-card-text>
      <v-alert
          type="warning"
          title="Important Info for Previous QR Code"
      >
        If you have previously set up or used an authenticator app to access Claimslink, you will need to delete the previous code key before creating a new key with this QR Code.
        <br>
        <br>
        In some cases you may need to delete and reinstall the authenticator app you have previously used.
        <br>
        <br>
        If you are having persistent issues with the new key, ensure your phone's timezone is synchronized to the current timezone you are in.
      </v-alert>
      <v-row>
        <v-col md="4">
        </v-col>
        <v-col md="4">
          <img :src="data.mfaQrCodeUrl" alt="MFA QR Code" />
        </v-col>
        <v-col md="4">
        </v-col>
      </v-row>
      <v-spacer></v-spacer>
      <v-row>
        <v-col md="4">
        </v-col>
        <v-col md="4">
          <v-checkbox
              @update:model-value="data.mfaPreferredEditing=true; data.mfaPreferenceChangeApp=true;"
              v-if="data.mfaSmsEnabled"
              v-model="data.mfaPreferredApp"
              color="success"
              label="Preferred MFA Method?"
          ></v-checkbox>
        </v-col>
        <v-col md="4">
        </v-col>
      </v-row>
      <v-row>
        <v-col md="3">
        </v-col>
        <v-col md="6">
          <div class="text-subtitle-1 mb-1 align-center">
            Enter the 6 digit code from your authenticator app here:
          </div>
          <v-text-field
              v-model="data.mfaAppCode"
              :rules="[FormValidation.appCodeRules]"
              @input="acceptAppCode"
              :disabled="data.mfaAppCodeIsValidating"
          >
          </v-text-field>
        </v-col>
        <v-col md="3">
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
  <v-dialog
      v-model="data.mfaShowSms"
      max-width="1000"
      min-height="600"
      @click:outside="data.mfaShowSms = false; data.mfaEditing = false; data.mfaPreferredEditing=false; data.mfaPreferenceChangeSms=false; getCurrentUserMFA()"
  >
    <v-card class="overflow-y-hidden">
      <v-card-title class="d-flex justify-space-between align-center">
        Set SMS Phone Number
        <v-btn
            class="align-right"
            icon="mdi-close"
            variant="text"
            @click="data.mfaShowSms = false; data.mfaEditing = false; data.mfaPreferredEditing=false; data.mfaPreferenceChangeSms=false; getCurrentUserMFA()"
        ></v-btn>
      </v-card-title>
      <v-card-text>
        Enter the phone number you want to receive SMS messages on for Multi Factor Authentication when logging in to Claimslink.
        <br>
        <br>
        <v-alert type="warning">
          Please ensure this number is accurate.
        </v-alert>
        <v-checkbox
            class="mt-5"
            color="success"
            label="Enable text message MFA. By selecting this option, you agree to receive automated security/MFA text messages from Claimslink. Message and data rates may apply. Reply STOP to opt out."
            v-model="data.mfaTCPAChecked"
            :disabled="!data.mfaAppEnabled"
            @update:model-value="disableSms"
        ></v-checkbox>
        <v-checkbox @update:model-value="data.mfaPreferredEditing=true; data.mfaPreferenceChangeSms=true;" v-if="data.mfaTCPAChecked" v-model="data.mfaPreferredSms" color="success" label="Preferred MFA Method?"></v-checkbox>
        <v-row v-if="data.mfaTCPAChecked">
          <v-col md="4"></v-col>
          <v-col md="4">
            <v-text-field
                v-model="data.mfaPhoneNumber"
                label="Example: (123) 456-7890"
                :rules="[FormValidation.phoneRules]"
                @input="acceptSmsNumber"
            ></v-text-field>
          </v-col>
          <v-col md="4"></v-col>
        </v-row>
        <v-row v-if="data.mfaCognitoPhoneValid && !data.mfaSmsCodeSent" class="mt-2 mb-5 p-3">
          <v-col md="4"></v-col>
          <v-col md="4">
            <v-btn
                text="Send SMS Code"
                size="large"
                color="primary"
                prepend-icon="mdi-message"
                :loading="data.mfaSmsCodeIsValidating"
                :disabled="data.mfaSmsCodeIsValidating"
                @click="updateSmsPhoneNumber()"
            ></v-btn>
          </v-col>
          <v-col md="4"></v-col>
        </v-row>
        <v-row v-if="data.mfaCognitoPhoneUpdated && data.mfaSmsCodeSent">
          <v-col md="4"></v-col>
          <v-col md="4">
            <div class="text-subtitle-1 mb-1 align-center">
              Enter the 6 digit code that was sent to your phone:
            </div>
            <v-text-field
                v-model="data.mfaSmsCode"
                :rules="[FormValidation.appCodeRules]"
                @input="acceptSmsCode"
                :disabled="data.mfaSmsCodeIsValidating"
            >
            </v-text-field>
          </v-col>
          <v-col md="4"></v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
  <v-dialog
    v-model="data.showTextingEnableDialog"
    max-width="1000"
    persistent
  >
    <v-card variant="elevated" elevation="4">
      <v-card-title class="border-b">Enable Text Messaging</v-card-title>
      <v-card-text>
        <p class="text-medium-emphasis ma-0" style="color: #000;">
          AAN uses text messaging to contact you for claim assignments, inquiries on claim data, and day-to-day operations.<br/>
          <em class="text--black" style="color: #000;">Review your mobile phone number and verify it is correct.</em>
        </p>
        <p class="text-medium-emphasis ma-0 mt-2">
          By enabling text messages, I agree to receive dispatch communications, notifications, and alerts from Associated Adjusters Network LLC at the phone number provided.
          Message frequency varies. Message & data rates may apply. Reply <b>STOP</b> to opt out or HELP for help.
          For more detail, read the <a href="https://associatedadjusting.com/privacy" target="_blank">Privacy Policy</a> and the <a href="https://associatedadjusting.com/terms" target="_blank">Terms of Service</a>.
        </p>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn variant="elevated" elevation="4" class="mr-5 pl-4 pr-4" color="primary" @click="AcceptTextMessagingDialog">Enable Text Messaging</v-btn>
        <v-btn variant="elevated" elevation="4" class="pl-4 pr-4" color="default" @click="ReminderLaterDialog">Remind Me Later</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* The text-messages setting under the mobile number: a soft box with a left accent, so it reads as part of Mobile */
.texting-box {
  background: rgba(var(--v-theme-primary), 0.05);
  border-left: 3px solid rgb(var(--v-theme-primary));
}


.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

</style>